import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import Table from '@/components/table/Table';
import { COMMENT_IN_REVIEW_STATUS, DATE_YMD_HMS_12H_FORMAT, DEFAULT_SELECT_PAGE_SIZES, REVIEW_STATUS, ROUTES, SUBMIT_MOMENT_DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { REVIEW_STATUS_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { DropdownMenu } from '@radix-ui/react-dropdown-menu';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { CheckIcon, Edit, EllipsisIcon, InfoIcon, PlusIcon, SearchIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import { Link, useNavigate } from 'react-router-dom';
import { formatNumber, scrollToTop } from '@/utils/utils';
import Calendar from '@/components/calendar/Calendar';
import { Review } from '@/types/review';
import AddSeedingComment from './components/AddSeedingComment';
import EditSeedingComment from './components/EditSeedingComment';

export default observer(function ReviewManagementPage() {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        reviewStore: { reviews, getAllReviews, keepActivedTab, keepSearchParams, paging, setObservable, totalRecord, totalByStatus, defaultPaging },
        modalStore: { showModal }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        reviewerName: yup
            .string(),
        fromCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        toCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        shopName: yup
            .string(),
        servicesQuality: yup.array().of(yup.number()),
        commentInReview: yup.array().of(yup.boolean())
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        reviewerName: '',
        fromCreatedDate: '',
        toCreatedDate: '',
        shopName: '',
        servicesQuality: [],
        commentInReview: []
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    const watchFromCreateDate = form.watch('fromCreatedDate');
    const watchToCreateDate = form.watch('toCreatedDate');

    // State
    const [activeTab, setActiveTab] = useState(keepActivedTab ? keepActivedTab : '');
    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');

    // lifecycle
    useEffect(() => {
        if (keepSearchParams) {
            form.reset({
                ...keepSearchParams,
                fromCreatedDate: (keepSearchParams as any).fromCreatedDate ? new Date((keepSearchParams as any).fromCreatedDate) : '',
                toCreatedDate: (keepSearchParams as any).toCreatedDate ? new Date((keepSearchParams as any).toCreatedDate) : ''
            });
            onFetch(keepSearchParams, paging);
        }
    }, []);

    useEffect(() => {
        onFetch(searchState);
    }, [activeTab]);

    // function
    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        setObservable('keepActivedTab', activeTab);
        setObservable('keepSearchParams', toJS(searchParams));
        getAllReviews(convertFormdataToPayload(searchParams), paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleTabChange = (tabValue: string) => {
        setActiveTab(tabValue);
    };

    const handleReloadTable = () => {
        onFetch(searchState, paging); // Gọi lại hàm fetch để tải lại dữ liệu bảng
    };

    const handleClickAddSeedingComment = () => {
        showModal({
            size: '4xl',
            title: t('words_title.add_seeding_comment'),
            content: <AddSeedingComment onSuccess={handleReloadTable} />,
            footerClassName: 'hidden'
        });
    };

    const convertFormdataToPayload = (data: FormData) => {
        const { reviewerName, fromCreatedDate, toCreatedDate, shopName, servicesQuality, commentInReview } = data ?? {};

        const [include, notInclude] = commentInReview ?? [];
        const pickUpStatusConvert = include && notInclude ?
            [true, false] :
            include ?
                [true] :
                notInclude ?
                    [false] :
                    [];
        return {
            reviewerName,
            fromCreatedDate: formatDateTime(fromCreatedDate, SUBMIT_MOMENT_DATE_YMD, {
                onlyTime: true
            }),
            toCreatedDate: formatDateTime(toCreatedDate, SUBMIT_MOMENT_DATE_YMD, {
                onlyTime: true
            }),
            shopName,
            servicesQuality,
            commentInReview: pickUpStatusConvert,
            status: activeTab,
            is_seeding_comment: String(activeTab) === String(REVIEW_STATUS[REVIEW_STATUS_TYPE.SeedingComments].value)
        };
    };

    const handleDisableTime = (date: Date, limitDate: string | Date | undefined, isToDate: boolean) => {
        if (limitDate) {
            return isToDate ? date < limitDate : date > limitDate;
        }
        return false;
    };

    // columns
    const columns: ColumnDef<Review>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'shopName',
            header: t('words_title.shop_name'),
            enableSorting: false,
            size: 250,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original?.shop?.isDeleted ?
                            (
                                <div>{original?.shop?.name ?? ''}</div>
                            ) :
                            (
                                <Link to={`/shops/${original.shop.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                    {original?.shop?.name ?? ''}
                                </Link>
                            )
                    }
                </>
            )
        },
        {
            accessorKey: 'reviewer',
            header: t('words_title.reviewer'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original?.user?.isDeleted ?
                            (
                                <div>{original?.user?.fullName ?? ''}</div>
                            ) :
                            (
                                <Link to={`/users/${original.user.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                    {original?.user?.fullName ?? ''}
                                </Link>
                            )
                    }
                </>
            )
        },
        {
            accessorKey: 'serviceQuality',
            header: t('words_title.review_rating'),
            enableSorting: false,
            size: 50,
            cell: ({ row: { original } }) => {
                return (
                    <div className='flex items-center gap-1'>
                        {Array.from({ length: 5 }, (_, index) => {
                            const currentIndex = index + 1;
                            return (
                                <StarIcon
                                    key={index}
                                    className={classNames(currentIndex <= original.serviceQuality ? 'fill-star-1 text-star-1' : 'text-star-1')}
                                />
                            );
                        })}
                    </div>
                );
            }
        },
        {
            accessorKey: 'comment_in_review',
            header: t('words_title.comment_in_review'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.commentInReview ? <CheckIcon className='h-4.5 w-4.5 text-accept-action' /> : ''}</div>
            )
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.created_date'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <div>{original.createdAt ? formatDateTime(original.createdAt, DATE_YMD_HMS_12H_FORMAT) : ''}</div>
            )
        },
        ...(activeTab === '3' ?
            [
                {
                    accessorKey: 'create_by',
                    header: t('words_title.create_by'),
                    enableSorting: false,
                    size: 150,
                    cell: ({ row: { original } }) => (
                        <div>{original?.createdBySeedingUser?.full_name}</div>
                    )
                }
            ] :
            [
                {
                    accessorKey: 'status',
                    header: t('words_title.status'),
                    enableSorting: false,
                    size: 150,
                    cell: ({ row: { original } }) => (
                        <ShopStatusColumn type={original.status} />
                    )
                }
            ]),
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown status={original.status} id={original.id} onFetch={onFetch} activeTab={activeTab} showModal={showModal} loadDataTable={handleReloadTable} />
                </div>
            )
        }
    ];

    const qualityOptions = [
        {
            id: 1,
            label: (
                <span className='flex items-center'>
                    {t('words_title.one_star')}
                    <StarIcon className='fill-star-1 text-star-1 ml-1' />
                </span>
            )
        },
        {
            id: 2,
            label: (
                <span className='flex items-center'>
                    {t('words_title.two_star')}
                    <StarIcon className='fill-star-1 text-star-1 ml-1' />
                </span>
            )
        },
        {
            id: 3,
            label: (
                <span className='flex items-center'>
                    {t('words_title.three_star')}
                    <StarIcon className='fill-star-1 text-star-1 ml-1' />
                </span>
            )
        },
        {
            id: 4,
            label: (
                <span className='flex items-center'>
                    {t('words_title.four_star')}
                    <StarIcon className='fill-star-1 text-star-1 ml-1' />
                </span>
            )
        },
        {
            id: 5,
            label: (
                <span className='flex items-center'>
                    {t('words_title.five_star')}
                    <StarIcon className='fill-star-1 text-star-1 ml-1' />
                </span>
            )
        }
    ] as const;

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>

                            <FormField
                                control={form.control}
                                name='reviewerName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.reviewer')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='flex items-center'>
                                <Label className='w-1/5 label-1'>{t('words_title.created_date')}</Label>
                                <div className='flex items-center gap-3 w-4/5'>
                                    <FormField
                                        control={form.control}
                                        name='fromCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchToCreateDate, false)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='toCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchFromCreateDate, true)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name='shopName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.shop_name')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='servicesQuality'
                                render={() => (
                                    <FormItem>
                                        <div className='flex items-center h-full'>
                                            <Label className='w-1/5 label-1'>{t('words_title.number_of_star')}</Label>
                                            <div className='grid grid-cols-5 w-4/5'>
                                                {qualityOptions.map(item => (
                                                    <FormField
                                                        key={item.id}
                                                        control={form.control}
                                                        name='servicesQuality'
                                                        render={({ field }) => {
                                                            return (
                                                                <FormItem
                                                                    key={item.id}
                                                                    className='flex flex-row items-center gap-1 w-1/5'
                                                                >
                                                                    <FormControl>
                                                                        <Checkbox
                                                                            checked={field.value?.includes(item.id)}
                                                                            onCheckedChange={(checked) => {
                                                                                return checked && field.value ?
                                                                                    field.onChange([...field.value, item.id]) :
                                                                                    field.onChange(
                                                                                        field.value?.filter(
                                                                                            value => value !== item.id
                                                                                        )
                                                                                    );
                                                                            }}
                                                                        />
                                                                    </FormControl>
                                                                    <Label className='font-normal'>
                                                                        {item.label}
                                                                    </Label>
                                                                </FormItem>
                                                            );
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='flex items-center mt-4'>
                                <Label className='w-1/5 label-1'>{t('words_title.comment_in_review')}</Label>
                                <div className='w-4/5 flex items-center gap-8'>
                                    {
                                        Object.keys(COMMENT_IN_REVIEW_STATUS).map((item, index) => (
                                            <FormField
                                                key={index}
                                                control={form.control}
                                                name={`commentInReview.${index}`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <div className='flex gap-2 items-center'>
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value}
                                                                    onCheckedChange={field.onChange}
                                                                />
                                                            </FormControl>
                                                            <Label>{t(`words_title.${COMMENT_IN_REVIEW_STATUS[item]?.key}`)}</Label>
                                                        </div>
                                                    </FormItem>
                                                )}
                                            />
                                        ))
                                    }
                                </div>
                            </div>

                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full h-full flex flex-col'>
                <div className='bg-white mt-4 rounded p-4'>
                    <Button variant='default' className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto' onClick={handleClickAddSeedingComment}>
                        <PlusIcon className='w-7 h-7 text-white' />
                        <span>{t('button.add_seeding_comment')}</span>
                    </Button>
                    <TabsList variant='shop' data-orientation='horizontal' className='flex w-full mt-6'>
                        <TabsTrigger
                            variant='shop'
                            className='md:text-[0.875rem] text-3 w-full'
                            value=''
                        >
                            {`${t('words_title.all_review')} (${formatNumber(Number(totalByStatus[1] + totalByStatus[2]))})`}
                        </TabsTrigger>
                        {
                            Object.entries([
                                REVIEW_STATUS[REVIEW_STATUS_TYPE.Published],
                                REVIEW_STATUS[REVIEW_STATUS_TYPE.RequestApproval],
                                REVIEW_STATUS[REVIEW_STATUS_TYPE.SeedingComments]
                            ]).map(([key, value], index) => (
                                <TabsTrigger
                                    key={index}
                                    variant='shop'
                                    className='md:text-[0.875rem] text-3 w-full'
                                    value={value.value.toString()}
                                >
                                    {`${t(`words_title.${value.key}`)} (${formatNumber(totalByStatus?.[value.value] ?? 0)})`}
                                </TabsTrigger>
                            ))
                        }
                    </TabsList>
                </div>

                <div className='bg-white rounded p-4'>
                    <TabsContent value=''>
                        <Table
                            columns={columns}
                            data={reviews}
                            rowCount={totalRecord}
                            onStateChange={onStateChange}
                            state={paging}
                            selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                            getRowId={row => row.id?.toString()}
                        />
                    </TabsContent>
                    {
                        Object.entries([
                            REVIEW_STATUS[REVIEW_STATUS_TYPE.Published],
                            REVIEW_STATUS[REVIEW_STATUS_TYPE.RequestApproval],
                            REVIEW_STATUS[REVIEW_STATUS_TYPE.SeedingComments]
                        ]).map(([key, value], index) => (
                            <TabsContent value={value.value.toString()} key={index}>
                                <Table
                                    columns={columns}
                                    data={reviews}
                                    rowCount={totalRecord}
                                    onStateChange={onStateChange}
                                    state={paging}
                                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                                    getRowId={row => row.id?.toString()}
                                />
                            </TabsContent>
                        ))
                    }
                </div>
            </Tabs>
        </div>
    );
});

interface ShopStatusColumnProps {
    type: number
}

const ShopStatusColumn = ({ type }: ShopStatusColumnProps) => {
    // hooks
    const { t } = useTranslation();

    return (
        <div className='flex items-center justify-start gap-2'>
            <div className={classNames('w-2 h-2 rounded-full flex-none', REVIEW_STATUS[type].color)}></div>
            <div>{t(`words_title.${REVIEW_STATUS[type]?.key}`)}</div>
        </div>
    );
};

interface ActionMenuDropdownProps {
    status: number,
    id: string,
    onFetch: Function,
    showModal: Function,
    activeTab: string,
    loadDataTable: () => void
}

const ActionMenuDropdown = ({ status, id, onFetch, showModal, activeTab, loadDataTable }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        reviewStore: { publishReview, deleteReview, keepSearchParams, paging },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    // function
    const handleClickPublished = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_published_review'),
            onSave: async () => {
                const res = await flowResult(publishReview(id));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickDelete = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_delete_review'),
            onSave: async () => {
                const res = await flowResult(deleteReview(id));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickView = async () => {
        navigate(`${ROUTES.reviewManagement.href}/${id}`);
    };

    const handleClickEdit = async () => {
        showModal({
            size: '4xl',
            title: t('words_title.edit_seeding_comment'),
            content: <EditSeedingComment shopId={id} onSuccess={loadDataTable} />,
            footerClassName: 'hidden'
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>
                {
                    status === REVIEW_STATUS_TYPE.RequestApproval && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={CheckIcon} text={t('words_title.published_action')} iconColor='text-accept-action' onClick={handleClickPublished} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                {
                    activeTab !== '3' && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={InfoIcon} text={t('words_title.view_action')} onClick={handleClickView} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                {
                    activeTab === '3' && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={Edit} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};
